import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { ClassroomInSchool, MyEnrollRequest } from '../../../lib/classroomsApi';

interface Props {
  classes: ClassroomInSchool[];
  enrollRequests: MyEnrollRequest[];
  isLoading: boolean;
  error: string | null;
  info: string | null;
  isB2B?: boolean;
  onCreateClassroom: (name: string) => Promise<void>;
  onJoin: (classId: number) => Promise<void>;
  onLeave: (classId: number) => Promise<void>;
  onOpenClass: (classId: number) => void;
  onRename: (classId: number, currentName: string) => void;
  onDelete: (classId: number) => void;
  onAddAdmin: (classId: number) => void;
  requestCreateOpen?: boolean;
  onConsumeCreateOpen?: () => void;
}

const ClassroomsListView: React.FC<Props> = ({
  classes,
  enrollRequests,
  isLoading,
  error,
  info,
  isB2B,
  onCreateClassroom,
  onJoin,
  onLeave,
  onOpenClass,
  onRename,
  onDelete,
  onAddAdmin,
  requestCreateOpen,
  onConsumeCreateOpen,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);

  const enrollStatusByClassId = useMemo(() => {
    const map = new Map<number, MyEnrollRequest>();
    for (const r of enrollRequests) {
      if (typeof r.classroom_id === 'number') map.set(r.classroom_id, r);
    }
    return map;
  }, [enrollRequests]);

  React.useEffect(() => {
    if (requestCreateOpen && !isB2B) {
      setShowCreateModal(true);
      onConsumeCreateOpen?.();
    }
  }, [requestCreateOpen, onConsumeCreateOpen, isB2B]);

  const submitCreate = async () => {
    if (isB2B) return;
    const name = createName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      await onCreateClassroom(name);
      setShowCreateModal(false);
      setCreateName('');
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clase</h1>
          <p className="text-sm text-muted-foreground">
            Toate clasele din școala ta sunt vizibile. Te poți înscrie în oricare dintre ele.
          </p>
        </div>
        {isB2B ? (
          <div className="text-xs text-muted-foreground text-right max-w-[340px]">
            Mod B2B: clasele sunt create de secretariat.
          </div>
        ) : (
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
            Creează clasă
          </Button>
        )}
      </div>

      {isLoading && <div className="text-sm text-muted-foreground mb-4">Se încarcă…</div>}
      {error && (
        <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>
      )}
      {info && (
        <div className="mb-4 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {classes.map((c) => {
          const req = enrollStatusByClassId.get(c.id);
          const status = req?.status;
          const isAccepted = status === 'accepted' || c.is_member;

          return (
            <div
              key={c.id}
              className={`bg-card border border-border rounded-lg p-5 ${c.is_admin ? 'ring-1 ring-primary/30' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-foreground truncate">{c.name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">Profesor: {c.profesor}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {c.is_admin && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-primary/10 text-primary">
                        Admin
                      </span>
                    )}
                    {c.is_member && !c.is_admin && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-muted text-foreground">
                        Membru
                      </span>
                    )}
                  </div>
                </div>

                {c.is_admin && (
                  <div className="flex items-center gap-1">
                    {!isB2B && (
                      <button
                      className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                      onClick={() => onRename(c.id, c.name)}
                      aria-label="Redenumește clasa"
                    >
                      <Icon name="Pencil" size={18} className="text-muted-foreground" />
                      </button>
                    )}
                    <button
                      className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                      onClick={() => onAddAdmin(c.id)}
                      aria-label="Promovează profesor ca admin"
                    >
                      <Icon name="UserCog" size={18} className="text-muted-foreground" />
                    </button>
                    {!isB2B && (
                      <button
                      className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                      onClick={() => onDelete(c.id)}
                      aria-label="Șterge clasa"
                    >
                      <Icon name="Trash2" size={18} className="text-error" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Rating clasă</span>
                <span className="font-medium text-foreground flex items-center gap-2">
                  <span>{typeof c.ratingPercent === 'number' ? `${c.ratingPercent}%` : '—'}</span>
                  {typeof c.ratingPercent === 'number' && (
                    <Icon
                      name={(c.ratingTrend || 'flat') === 'up' ? 'TrendingUp' : (c.ratingTrend || 'flat') === 'down' ? 'TrendingDown' : 'Minus'}
                      size={16}
                      className={
                        typeof c.ratingDelta === 'number'
                          ? (c.ratingTrend || 'flat') === 'up'
                            ? 'text-success'
                            : (c.ratingTrend || 'flat') === 'down'
                              ? 'text-error'
                              : 'text-muted-foreground'
                          : 'text-muted-foreground'
                      }
                      title={
                        typeof c.ratingDelta === 'number'
                          ? (c.ratingTrend || 'flat') === 'up'
                            ? `În urcare (+${c.ratingDelta} pp, ultimele ${c.ratingTrendWindowDays ?? 30} zile)`
                            : (c.ratingTrend || 'flat') === 'down'
                              ? `În coborâre (${c.ratingDelta} pp, ultimele ${c.ratingTrendWindowDays ?? 30} zile)`
                              : `Stagnare (0 pp, ultimele ${c.ratingTrendWindowDays ?? 30} zile)`
                          : `Nu există date anterioare pentru comparație (ultimele ${c.ratingTrendWindowDays ?? 30} zile).`
                      }
                    />
                  )}
                </span>
              </div>

              <div className="mt-4 space-y-2">
                {isAccepted ? (
                  <Button
                    variant="default"
                    fullWidth
                    iconName="BookOpen"
                    iconPosition="left"
                    onClick={() => onOpenClass(c.id)}
                  >
                    Vezi clasa
                  </Button>
                ) : status === 'pending' ? (
                  <Button variant="outline" fullWidth disabled iconName="Clock" iconPosition="left">
                    Cerere trimisă
                  </Button>
                ) : status === 'rejected' ? (
                  <div className="text-sm text-error bg-error/10 border border-error/20 rounded-md p-3">
                    Cerere respinsă. Contactează profesorul administrator al clasei.
                  </div>
                ) : (
                  <Button
                    variant="default"
                    fullWidth
                    iconName="UserPlus"
                    iconPosition="left"
                    onClick={() => onJoin(c.id)}
                  >
                    Aderă la clasă
                  </Button>
                )}

                {c.is_member && (
                  <Button variant="outline" fullWidth iconName="LogOut" iconPosition="left" onClick={() => onLeave(c.id)}>
                    Părăsește clasa
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Creează clasă</h3>
              <button
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                onClick={() => setShowCreateModal(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <Input
                label="Numele clasei"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Ex: a VIII-a B"
              />
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Anulează
                </Button>
                <Button variant="default" onClick={submitCreate} disabled={createBusy || !createName.trim()}>
                  Creează
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomsListView;
